import React from "react";

export type AnswerType =
  | "text" | "date" | "textarea" | "radio" | "checkbox" | "select"
  | "longtext" | "number" | "email" | "phone"
  | "singleselect" | "multiselect"
  // Described choices, rendered as cards rather than bare radios. The intake
  // questionnaires ask things like "wordmark or lettermark or emblem", where
  // the label alone means nothing to most clients and the description is doing
  // the actual work.
  | "cards" | "cards-multi"
  // Opposing pairs — traditional against modern, playful against serious.
  // A position, not a choice, so a slider rather than options.
  | "slider-pairs";

export interface OptionDetail {
  value: string;
  label: string;
  description?: string;
}

export interface SliderPair {
  left: string;
  right: string;
}

export interface Question {
  _id: string;
  question: string;
  answerType: AnswerType;
  options?: string[];
  required: boolean;
  disabled?: boolean;
  placeholder?: string;
  helpText?: string;
  optionDetails?: OptionDetail[];
  pairs?: SliderPair[];
}

interface QuestionInputProps {
  question: Question;
  value?: string | string[];
  onChange: (value: string | string[]) => void;
  disabled?: boolean;
  required?: boolean;
  // Brand colour of the environment the questionnaire belongs to.
  accent?: string;
}

const DEFAULT_ACCENT = "#0058c3";

const baseInputClasses =
  "w-full px-4 py-3 text-[14px] text-slate-800 bg-white border border-slate-200 rounded-xl outline-none transition-all " +
  "placeholder:text-slate-400 hover:border-slate-300 focus:border-[color:var(--q-accent)] " +
  "focus:ring-4 focus:ring-[color:var(--q-accent)]/12 disabled:bg-slate-50 disabled:cursor-not-allowed";

const QuestionInput: React.FC<QuestionInputProps> = ({ question, value, onChange, disabled, required, accent }) => {
  const valString = typeof value === "string" ? value : "";
  const valArray = Array.isArray(value) ? value : [];
  // The accent is passed down as a CSS variable so focus/selection states match
  // the client's brand without any per-brand code.
  const style = { ["--q-accent" as any]: accent || DEFAULT_ACCENT } as React.CSSProperties;

  // The newer type names map onto the renderers that already exist. Keeping one
  // vocabulary in the templates and one set of renderers here means a template
  // author never has to know which era of the system a type came from.
  const type = ({
    longtext: "textarea",
    singleselect: "radio",
    multiselect: "checkbox",
    email: "text",
    phone: "text",
    number: "text",
  } as Record<string, string>)[question.answerType] || question.answerType;

  // ── Described choices ─────────────────────────────────────────────────────
  if (type === "cards" || type === "cards-multi") {
    const multi = type === "cards-multi";
    const details: OptionDetail[] =
      question.optionDetails?.length
        ? question.optionDetails
        : (question.options || []).map((o) => ({ value: o, label: o }));

    const isOn = (v: string) => (multi ? valArray.includes(v) : valString === v);
    const toggle = (v: string) => {
      if (disabled) return;
      if (!multi) return onChange(v);
      onChange(valArray.includes(v) ? valArray.filter((x) => x !== v) : [...valArray, v]);
    };

    return (
      <div style={style} className="grid sm:grid-cols-2 gap-2.5">
        {details.map((d) => {
          const on = isOn(d.value);
          return (
            <button
              key={d.value}
              type="button"
              onClick={() => toggle(d.value)}
              disabled={disabled}
              aria-pressed={on}
              className={`text-left p-3.5 rounded-xl border-2 transition-all disabled:opacity-60 ${
                on
                  ? "border-[color:var(--q-accent)] bg-[color:var(--q-accent)]/[0.06]"
                  : "border-slate-200 bg-white hover:border-slate-300"
              }`}
            >
              <span className="flex items-start gap-2.5">
                <span
                  className={`shrink-0 mt-0.5 w-4 h-4 grid place-items-center border-2 transition-colors ${
                    multi ? "rounded" : "rounded-full"
                  } ${on ? "border-[color:var(--q-accent)] bg-[color:var(--q-accent)]" : "border-slate-300"}`}
                >
                  {on && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
                </span>
                <span className="min-w-0">
                  <span className="block text-[14px] font-semibold text-slate-800">{d.label}</span>
                  {d.description && (
                    <span className="block text-[12.5px] text-slate-500 mt-0.5 leading-relaxed">
                      {d.description}
                    </span>
                  )}
                </span>
              </span>
            </button>
          );
        })}
      </div>
    );
  }

  // ── Opposing-pair sliders ─────────────────────────────────────────────────
  if (type === "slider-pairs") {
    // Stored as "Left↔Right:value" per pair, so one answer field holds the whole
    // set and no schema change is needed to add or remove a pair.
    const parsed: Record<string, number> = {};
    for (const part of valArray.length ? valArray : String(valString || "").split("|")) {
      const [k, v] = String(part).split(":");
      if (k && v !== undefined) parsed[k] = Number(v);
    }
    const pairs = question.pairs || [];
    const keyOf = (p: SliderPair) => `${p.left}↔${p.right}`;

    const setPair = (p: SliderPair, v: number) => {
      if (disabled) return;
      const next = { ...parsed, [keyOf(p)]: v };
      onChange(pairs.map((x) => `${keyOf(x)}:${next[keyOf(x)] ?? 50}`));
    };

    return (
      <div style={style} className="space-y-4">
        {pairs.map((p) => {
          const v = parsed[keyOf(p)] ?? 50;
          return (
            <div key={keyOf(p)}>
              <div className="flex items-center justify-between text-[12.5px] font-semibold text-slate-600 mb-1">
                <span>{p.left}</span>
                <span>{p.right}</span>
              </div>
              <input
                type="range"
                min={0}
                max={100}
                step={5}
                value={v}
                disabled={disabled}
                onChange={(e) => setPair(p, Number(e.target.value))}
                className="w-full accent-[color:var(--q-accent)] cursor-pointer disabled:cursor-not-allowed"
              />
            </div>
          );
        })}
      </div>
    );
  }

  switch (type) {
    case "text":
      return (
        <input
          type="text"
          value={valString}
          placeholder={question.placeholder || ""}
          onChange={(e) => onChange(e.target.value)}
          className={baseInputClasses}
          style={style}
          disabled={disabled}
          required={required}
        />
      );

    case "date":
      return (
        <input
          type="date"
          value={valString}
          placeholder={question.placeholder || ""}
          onChange={(e) => onChange(e.target.value)}
          className={baseInputClasses}
          style={style}
          disabled={disabled}
          required={required}
        />
      );

    case "textarea":
      return (
        <textarea
          value={valString}
          placeholder={question.placeholder || ""}
          onChange={(e) => onChange(e.target.value)}
          className={`${baseInputClasses} h-28 resize-y leading-relaxed`}
          style={style}
          disabled={disabled}
          required={required}
        />
      );

    // Options render as selectable cards — far easier to tap on mobile than a
    // bare radio/checkbox, and the selected state shows the brand colour.
    case "radio":
    case "checkbox": {
      const isChecked = (opt: string) =>
        question.answerType === "radio" ? valString === opt : valArray.includes(opt);
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" style={style}>
          {question.options?.map((opt, i) => {
            const checked = isChecked(opt);
            return (
              <label
                key={i}
                className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl border text-[14px] cursor-pointer transition-all ${
                  checked
                    ? "border-[color:var(--q-accent)] bg-[color:var(--q-accent)]/[0.06] text-slate-900 font-medium"
                    : "border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50"
                } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <input
                  type={question.answerType}
                  value={opt}
                  checked={checked}
                  disabled={disabled}
                  required={required && question.answerType === "radio"}
                  onChange={(e) => {
                    if (question.answerType === "radio") {
                      onChange(opt);
                    } else {
                      const newValue = [...valArray];
                      if (e.target.checked) newValue.push(opt);
                      else newValue.splice(newValue.indexOf(opt), 1);
                      onChange(newValue);
                    }
                  }}
                  className="h-4 w-4 shrink-0"
                  style={{ accentColor: accent || DEFAULT_ACCENT }}
                />
                <span className="leading-snug">{opt}</span>
              </label>
            );
          })}
        </div>
      );
    }

    case "select":
      return (
        <select
          value={valString}
          onChange={(e) => onChange(e.target.value)}
          className={`${baseInputClasses} appearance-none bg-[length:16px] bg-[right_14px_center] bg-no-repeat pr-10`}
          style={{
            ...style,
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2.5' stroke-linecap='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")",
          }}
          disabled={disabled}
          required={required}
        >
          <option value="">{question.placeholder || "Select an option"}</option>
          {question.options?.map((opt, i) => (
            <option key={i} value={opt}>{opt}</option>
          ))}
        </select>
      );

    default:
      return null;
  }
};

export default QuestionInput;
